import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import AdvertiserPanel from "./AdvertiserPanel";
import MessageBox from "../../components/MessageBox";
import Select from "react-select";
import ResponseLoader from "../../components/Loader/ResponseLoader";
import AdminCustomButton from "./Button/AdminCustomButton";
import AdminSaveHeader from "./CommonComponents/AdminSaveHeader";
import MessageHeading from "./CommonComponents/MessageHeading";
import MessageLabel from "./CommonComponents/MessageLabel";
import FormContainer from "./CommonComponents/FormContainer";
import LeftWrapper from "./CommonComponents/LeftWrapper";
import RightWrapper from "./CommonComponents/RightWrapper";
import Wrapper from "./CommonComponents/Wrapper";
import AdminCustomInput from "./CommonComponents/AdminCustomInput";
import AdminBackNavigation from "./CommonComponents/AdminBackNavigation";

const parseTargeting = (targeting) => {
    if (!targeting) {
        return { geo: "", device: "", video_ids: [] };
    }

    if (typeof targeting === "string") {
        try {
            const parsed = JSON.parse(targeting);
            return parsed && typeof parsed === "object"
                ? parsed
                : { geo: "", device: "", video_ids: [] };
        } catch (error) {
            console.warn("Failed to parse campaign targeting:", error);
            return { geo: "", device: "", video_ids: [] };
        }
    }

    return typeof targeting === "object"
        ? targeting
        : { geo: "", device: "", video_ids: [] };
};

const SSAICampaignEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const SSAI_API_URL = process.env.REACT_APP_SSAI_API_URL ;

    const [form, setForm] = useState({
        name: "",
        vastTagUrl: "",
        adType: "mid_roll",
        type: "linear",
        budget: "",
        startDate: "",
        endDate: "",
        status: "active",
        targeting: {
            geo: "",
            device: "",
            video_ids: ""
        }
    });

    const [errors, setErrors] = useState({});

    const adTypeOptions = [
        { value: "pre_roll", label: "Pre-Roll (Before Stream)" },
        { value: "mid_roll", label: "Mid-Roll (Ad Breaks)" },
        { value: "post_roll", label: "Post-Roll (After Stream)" },
        { value: "overlay", label: "Overlay (Non-Linear)" },
    ];

    const typeOptions = [
        { value: "linear", label: "Linear (Video Ad)" },
        { value: "non_linear", label: "Non-Linear (Static/Text)" },
        { value: "companion", label: "Companion (Side Banner)" },
    ];

    const statusOptions = [
        { value: "active", label: "Active" },
        { value: "paused", label: "Paused" },
        { value: "completed", label: "Completed" },
        { value: "draft", label: "Draft" },
    ];

    const selectStyles = {
        control: (base, state) => ({
            ...base,
            backgroundColor: 'transparent',
            borderColor: state.isFocused ? 'currentColor' : 'currentColor',
            minHeight: 48,
            boxShadow: 'none',
        }),
        menu: (base) => ({
            ...base,
            backgroundColor: 'var(--admin-select-menu-bg, transparent)',
            zIndex: 20
        }),
        singleValue: (base) => ({
            ...base,
            color: 'inherit'
        }),
        input: (base) => ({
            ...base,
            color: 'inherit'
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isFocused ? 'var(--admin-select-option-hover, rgba(0,0,0,0.08))' : 'transparent',
            color: 'inherit'
        })
    };

    const fetchCampaign = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${SSAI_API_URL}/api/advertiser/campaigns/${id}`, {
                headers: { 'x-api-key': process.env.REACT_APP_SSAI_API_KEY || 'ssai_secure_key_2024' }
            });
            if (res.data && res.data.success) {
                const data = res.data.data;
                const targeting = parseTargeting(data.targeting);

                setForm({
                    name: data.name || "",
                    vastTagUrl: data.vastTagUrl || "",
                    adType: data.adType || "mid_roll",
                    type: data.type || "linear",
                    budget: data.budget || "",
                    startDate: data.startDate ? data.startDate.split('T')[0] : "",
                    endDate: data.endDate ? data.endDate.split('T')[0] : "",
                    status: data.status || "active",
                    targeting: {
                        geo: targeting.geo || "",
                        device: targeting.device || "",
                        video_ids: Array.isArray(targeting.video_ids)
                            ? targeting.video_ids.join(', ')
                            : ""
                    }
                });
            }
        } catch (error) {
            console.error("Error fetching campaign:", error);
            toast.error("Failed to load campaign details.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCampaign();
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name.startsWith("targeting.")) {
            const field = name.split(".")[1];
            setForm(prev => ({
                ...prev,
                targeting: { ...prev.targeting, [field]: value }
            }));
        } else {
            setForm(prev => ({ ...prev, [name]: value }));
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!form.name) newErrors.name = "Campaign name is required";
        if (!form.vastTagUrl) newErrors.vastTagUrl = "VAST URL is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        try {
            await axios.put(`${SSAI_API_URL}/api/advertiser/campaigns/${id}`, {
                ...form,
                budget: form.budget ? parseFloat(form.budget) : null,
                targeting: {
                    ...form.targeting,
                    video_ids: form.targeting.video_ids
                        ? form.targeting.video_ids.split(',').map(v => v.trim()).filter(v => v)
                        : []
                }
            }, {
                headers: { 'x-api-key': process.env.REACT_APP_SSAI_API_KEY || 'ssai_secure_key_2024' }
            });
            toast.success("SSAI Campaign updated successfully!");
            navigate("/ssai-campaigns");
        } catch (error) {
            console.error("Error updating campaign:", error);
            toast.error("Failed to update campaign.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <AdvertiserPanel>
            <div className="p-3 rounded-3">
                {loading && <ResponseLoader />}
                <AdminBackNavigation path="/ssai-campaigns" text="Back" />
                <AdminSaveHeader heading="Edit SSAI Ad Campaign" saveMethod={handleSubmit} saveText="Update Campaign" />
                <form onSubmit={handleSubmit} className="mt-3">
                    <FormContainer>
                        <LeftWrapper>
                            <Wrapper>
                                <MessageHeading text="Basic Information" />
                                <MessageLabel text="Campaign Name" className="before-star" noMargin />
                                <AdminCustomInput
                                    type="text"
                                    name="name"
                                    value={form.name}
                                    onChange={handleChange}
                                    error={errors.name}
                                />

                                <MessageLabel text="VAST Ad Tag URL" className="before-star mt-2" noMargin />
                                <AdminCustomInput
                                    type="text"
                                    name="vastTagUrl"
                                    value={form.vastTagUrl}
                                    onChange={handleChange}
                                    error={errors.vastTagUrl}
                                />

                                <MessageBox
                                    classname="theme-text-color opacity-75 admin-input-description d-block mt-2"
                                    text="Supported Macros: [USER_ID], [IP_ADDRESS], [TIMESTAMP], [LAT_LONG], [DEVICE_ID]"
                                />
                            </Wrapper>

                            <Wrapper>
                                <MessageHeading text="Status & Placement" />

                                <MessageLabel text="Campaign Status" noMargin />
                                <Select
                                    options={statusOptions}
                                    value={statusOptions.find(o => o.value === form.status)}
                                    onChange={(opt) => setForm(prev => ({ ...prev, status: opt.value }))}
                                    className="mb-3 theme-text-color"
                                    classNamePrefix="admin-select"
                                    styles={selectStyles}
                                />

                                <MessageLabel text="Ad Type" noMargin />
                                <Select
                                    options={adTypeOptions}
                                    value={adTypeOptions.find(o => o.value === form.adType)}
                                    onChange={(opt) => setForm(prev => ({ ...prev, adType: opt.value }))}
                                    className="mb-3 theme-text-color"
                                    classNamePrefix="admin-select"
                                    styles={selectStyles}
                                />

                                <MessageLabel text="Creative Category" noMargin />
                                <Select
                                    options={typeOptions}
                                    value={typeOptions.find(o => o.value === form.type)}
                                    onChange={(opt) => setForm(prev => ({ ...prev, type: opt.value }))}
                                    className="theme-text-color"
                                    classNamePrefix="admin-select"
                                    styles={selectStyles}
                                />
                            </Wrapper>
                        </LeftWrapper>

                        <RightWrapper>
                            <Wrapper>
                                <MessageHeading text="Scheduling & Budget" />

                                <FormContainer className="mx-0">
                                    <LeftWrapper custom className="col-12 col-sm-6 ps-0 pe-sm-2">
                                        <MessageLabel text="Start Date" noMargin />
                                        <AdminCustomInput
                                            type="date"
                                            name="startDate"
                                            value={form.startDate}
                                            onChange={handleChange}
                                        />
                                    </LeftWrapper>
                                    <RightWrapper custom className="col-12 col-sm-6 pe-0 ps-sm-2">
                                        <MessageLabel text="End Date" noMargin />
                                        <AdminCustomInput
                                            type="date"
                                            name="endDate"
                                            value={form.endDate}
                                            onChange={handleChange}
                                        />
                                    </RightWrapper>
                                </FormContainer>

                                <MessageLabel text="Campaign Budget ($)" className="mt-2" noMargin />
                                <AdminCustomInput
                                    type="number"
                                    name="budget"
                                    value={form.budget}
                                    onChange={handleChange}
                                />
                            </Wrapper>

                            <Wrapper>
                                <MessageHeading text="Targeting (Optional)" />

                                <MessageLabel text="Geographic Targeting (ISO Countries)" noMargin />
                                <AdminCustomInput
                                    type="text"
                                    name="targeting.geo"
                                    value={form.targeting.geo}
                                    onChange={handleChange}
                                    placeHolder="e.g. US, UK, CA"
                                />

                                <MessageLabel text="Device Targeting" noMargin />
                                <AdminCustomInput
                                    type="text"
                                    name="targeting.device"
                                    value={form.targeting.device}
                                    onChange={handleChange}
                                    placeHolder="e.g. SmartTV, Mobile, Desktop"
                                />

                                <MessageLabel text="Target Video IDs (Comma Separated)" noMargin />
                                <AdminCustomInput
                                    type="text"
                                    name="targeting.video_ids"
                                    value={form.targeting.video_ids}
                                    onChange={handleChange}
                                    placeHolder="e.g. 1, 2, 5"
                                />
                            </Wrapper>
                        </RightWrapper>
                    </FormContainer>

                    {/* <AdminCustomButton saveMethod={handleSubmit} saveText={"Update Campaign"} className={"mt-2"} /> */}
                </form>
            </div>
        </AdvertiserPanel>
    );
};

export default SSAICampaignEdit;
